/*
 * The home hub's vocabulary: its pipeline nodes, the five stages an isolate
 * passes through, what each state looks like, and who may do what.
 *
 * The stage and detail codes are the server's frozen contract
 * (routes/isolate_pipeline.py); the UI says each as
 * isolates:detail.<stage>.<code>. The role sets are copied from the routes
 * each action calls -- file:line beside each -- because hiding a button is
 * courtesy and the server is the control. A role set that drifts shows a
 * button the server refuses (a 403 toast), never a write that should not be.
 */
import { TYPE_COLOR, TYPE_ICON } from '../specimens/ProvenanceGraph';
import { STORAGE_UNITS } from '../specimens/StoragePanel';
import { can, rolesWith } from '../../utils/permissions';

/* ── stages and states ─────────────────────────────────────────────────── */

/** The five boxes of a row's pipeline strip, in order. */
export const STAGES = ['collection', 'culture', 'dna', 'identification', 'bank'];

/** isolate_pipeline.py's detail codes per stage (the frozen contract). */
export const DETAIL_CODES = {
  collection: ['submitted', 'intake', 'strain_only', 'draft', 'no_origin', 'origin_deleted'],
  culture: ['slanted', 'ready', 'awaiting_creation', 'growing', 'slow_growth', 'in_rescue', 'contaminated',
    'failed', 'no_plates'],
  dna: ['identified', 'sequenced', 'raw_only', 'sequencing', 'extracted', 'resequence', 'no_extract'],
  identification: ['approved', 'dna', 'unconfirmed', 'conflict', 'none'],
  bank: ['banked', 'specimen_storage', 'slants_unbanked', 'slants_lost', 'no_slants'],
};

/* A state is never said by colour alone: each has a glyph and a border
 * style too (dashed for what has not started, dotted for what the reader is
 * not shown). `unknown` is not the server's: it is a stage the server sent as
 * null -- culture, DNA and bank to a reader with no lab visibility, which are
 * unknown, not absent (isolate_pipeline.derive). */
export const STATE_STYLE = {
  done: { glyph: '✓', bg: 'var(--c-success-soft)', fg: 'var(--c-success-fg)', border: 'solid' },
  active: { glyph: '●', bg: 'var(--c-info-soft)', fg: 'var(--c-info-fg)', border: 'solid' },
  pending: { glyph: '○', bg: 'var(--c-bg-muted)', fg: 'var(--c-text-muted-fg)', border: 'dashed' },
  problem: { glyph: '!', bg: 'var(--c-danger-soft)', fg: 'var(--c-danger-fg)', border: 'solid' },
  unknown: { glyph: '?', bg: 'var(--c-bg-subtle)', fg: 'var(--c-text-muted-fg)', border: 'dotted' },
};
export const stateStyle = (state) => STATE_STYLE[state] || STATE_STYLE.pending;

/* ── the canvas ────────────────────────────────────────────────────────── */

const color = (type) => TYPE_COLOR[type] || TYPE_COLOR.custom;

/*
 * Lane 1, "Active isolates (in culture)": two triggers wired into the lab,
 * then DNA, then "ready for bank", whose out-port drops to lane 2.
 *
 * `states` are the hub counts a node offers as filters: the key in the
 * /specimens/hub payload, and the overview's `state=` for that node.
 *
 * The Lab node's own count is the isolates in culture (active.total, its
 * bare view=lab). Its "Plates to make" is a count of its own, not a part of
 * that total: a plate not yet poured is no culture, so an isolate whose
 * plates are all still to make awaits the lab (the Collection and Intake
 * nodes' "awaiting lab") and is counted there too
 * (isolate_pipeline.AWAITING_GROUPS).
 */
export const ACTIVE_NODES = [
  { key: 'collection', icon: '📷', color: color('field_collection'), count: (h) => h?.collection?.submitted_7d,
    states: ['submitted_7d', 'mine_7d', 'drafts_mine', 'awaiting_lab'], filters: ['awaiting_lab'] },
  { key: 'intake', icon: '📦', color: color('custom'), count: (h) => h?.intake?.received_7d,
    states: ['received_7d', 'awaiting_lab', 'info_only_7d'], filters: ['awaiting_lab'] },
  { key: 'lab', icon: TYPE_ICON.lab_subculture, color: color('lab_subculture'), count: (h) => h?.active?.total,
    states: ['awaiting_creation', 'growing', 'ready', 'problem'], payload: 'lab' },
  { key: 'dna_id', icon: TYPE_ICON.dna_analysis, color: color('dna_analysis'), count: null,
    states: ['extracted', 'sequenced', 'identified', 'conflict'], payload: 'dna_id' },
  { key: 'to_bank', icon: TYPE_ICON.storage, color: color('storage'),
    count: (h) => h?.active?.to_bank?.slants_unbanked, states: ['slants_unbanked'], payload: 'to_bank' },
];
export const ACTIVE_EDGES = [
  { id: 'e-col-lab', from: 'collection', to: 'lab' },
  { id: 'e-int-lab', from: 'intake', to: 'lab' },
  { id: 'e-lab-dna', from: 'lab', to: 'dna_id' },
  { id: 'e-dna-bank', from: 'dna_id', to: 'to_bank' },
];

/** Lane 2, "Bank collection (preserved)": the root and one node per unit. */
export const BANK_STATES = ['healthy', 'review_due', 'in_refresh', 'problem'];
export const BANK_UNITS = STORAGE_UNITS;
export const BANK_COLOR = color('storage');
export const BANK_ICON = TYPE_ICON.storage;

/** A node's count for one of its states, from the hub payload. null when the
 *  server said it cannot know (a lab_scope of 'none' has no slant counts). */
export function stateCount(hub, nodeKey, state) {
  if (!hub) return null;
  switch (nodeKey) {
    case 'collection': return hub.collection?.[state] ?? null;
    case 'intake': return hub.intake?.[state] ?? null;
    case 'lab': return hub.active?.lab?.[state] ?? null;
    case 'dna_id': return hub.active?.dna_id?.[state] ?? null;
    case 'to_bank': return hub.active?.to_bank?.[state] ?? null;
    case 'bank': return hub.bank?.status?.[state] ?? null;
    default: return null;
  }
}

/** The DNA node has no total of its own: each isolate with an extract is in
 *  exactly one of its DNA states (extracted, sequenced, identified), so the
 *  header is their sum. "conflict" is an identification state of isolates
 *  already counted there, so it is left out of the sum. */
export function nodeCount(hub, node) {
  if (node.key === 'dna_id') {
    const d = hub?.active?.dna_id;
    if (!d) return null;
    const values = node.states.filter((s) => s !== 'conflict').map((s) => d[s])
      .filter((v) => typeof v === 'number');
    return values.length ? values.reduce((a, v) => a + v, 0) : null;
  }
  return node.count ? node.count(hub) ?? null : null;
}

/* ── where a click goes (URL state) ────────────────────────────────────── */

/** The URL parameters that filter the hub's one table to a node's rows, or
 *  null for a node that navigates away (Collection opens Field Mode). A
 *  trigger's count keeps its own origin (origin=), so the table lists
 *  exactly what it counted: the Collection node's "awaiting lab" is field
 *  collections only, not every isolate awaiting the lab. */
export function nodeParams(key, state) {
  switch (key) {
    case 'collection': return state ? { view: 'lab', node: 'lab', state, origin: 'field' } : null;
    case 'intake': return { view: 'intake', ...(state ? { state, origin: 'external' } : {}) };
    case 'lab': return { view: 'lab', ...(state ? { node: 'lab', state } : {}) };
    case 'dna_id': return { view: 'lab', node: 'dna_id', ...(state ? { state } : {}) };
    case 'to_bank': return { view: 'lab', node: 'to_bank', state: 'slants_unbanked' };
    case 'bank': return { view: 'bank', ...(state ? { state } : {}) };
    default: return key && key.startsWith('unit:') ? { view: 'bank', unit: key.slice(5) } : null;
  }
}

/** Which node the URL filters by: its header is pressed. None for the
 *  whole table (view "all") or "My isolates". */
export function selectedNode({ view, node, unit } = {}) {
  if (view === 'lab') return node === 'dna_id' || node === 'to_bank' ? node : 'lab';
  if (view === 'bank') return unit ? `unit:${unit}` : 'bank';
  if (view === 'intake') return 'intake';
  return null;
}

/** The state chips the table offers under a node's filter. The whole table
 *  offers none: the canvas's nodes and counts are its filters. */
export function tableStates(view, node) {
  if (view === 'lab' && node === 'dna_id') return ['extracted', 'sequenced', 'identified', 'conflict'];
  if (view === 'lab' && node === 'to_bank') return ['slants_unbanked'];
  if (view === 'lab') return ['awaiting_creation', 'growing', 'ready', 'problem', 'awaiting_lab'];
  if (view === 'bank') return BANK_STATES;
  if (view === 'intake') return ['awaiting_lab'];
  return [];
}

/* ── who may do what (copied from the routes) ──────────────────────────── */

const has = (user, roles) => (user?.roles || []).some((r) => roles.includes(r));

// routes/plates.py:161 ELEVATED_ROLES (create, activate, edit, from-slant);
// students pass create/from-slant for an assigned sample (plates.py:164-178).
export const PLATE_WRITE_ROLES = ['collection_manager', 'lab_technician', 'lab_manager'];
const PLATE_CREATE_ROLES = [...PLATE_WRITE_ROLES, 'student'];
// routes/plate_checks.py:11 LAB_ROLES -- lab:observe.
export const CHECK_ROLES = ['collection_manager', 'lab_technician', 'student', 'lab_manager',
  'authorized_researcher', 'system_admin'];
// The scope "slant:cut", plus an assigned student (routes/slants.py _student_refusal).
const SLANT_CREATE_ROLES = [...rolesWith('slant:cut'), 'student'];
// routes/slants.py ELEVATED_ROLES: PUT /slants/{id}/storage, lab writers only.
const SLANT_STORAGE_ROLES = PLATE_WRITE_ROLES;
// routes/slants.py:742: POST /slants/deposit (deposit_slants) holds the
// storage route's lab writers, ELEVATED_ROLES, even when it cuts the set: a
// student who may cut slants still may not bank them.
const DEPOSIT_ROLES = SLANT_STORAGE_ROLES;
// routes/dna.py:113 ELEVATED_ROLES; routes/dna_sequencing_runs.py:10 the same.
const DNA_WRITE_ROLES = PLATE_WRITE_ROLES;
// routes/identifications.py:11-12: identifiers, and who may approve. Anyone
// else identifies only their own collection (identifications.py:16-32).
const IDENTIFY_ROLES = ['collection_manager', 'internal_expert', 'lab_technician'];
export const APPROVER_ROLES = ['collection_manager', 'internal_expert'];
// routes/external.py:147 CREATE_ROLES; routes/bulk_import.py:100 IMPORT_ROLES.
const INTAKE_ROLES = ['collection_manager', 'internal_expert', 'lab_technician', 'lab_manager'];
const IMPORT_ROLES = rolesWith('import:run');

/** May this user take this quick action on this isolate? */
export function canDo(user, action, item = {}) {
  switch (action) {
    case 'logCheck':
    case 'conditions':
    case 'labPhoto':
    case 'micrograph':
      // A micrograph is routes/microscopy.py register: lab:observe, the same set.
      return has(user, CHECK_ROLES);
    case 'editPlate':
    case 'activate':
      return has(user, PLATE_WRITE_ROLES);
    case 'startPlate':
    case 'revive':
      return has(user, PLATE_CREATE_ROLES);
    case 'cutSlants':
      return has(user, SLANT_CREATE_ROLES);
    case 'setStorage':
      return has(user, SLANT_STORAGE_ROLES);
    case 'deposit':
      return has(user, DEPOSIT_ROLES);
    case 'logReview':
      return can(user, 'lab:observe');
    case 'extractDna':
    case 'addRun':
      return has(user, DNA_WRITE_ROLES);
    case 'identify':
      return !!item.sample_id && (has(user, IDENTIFY_ROLES)
        || (!!user?.user_id && String(item.collector_id) === String(user.user_id)));
    case 'approve':
      return has(user, APPROVER_ROLES);
    case 'specimenStorage':
      return can(user, 'specimen:write');
    case 'newIntake':
      return has(user, INTAKE_ROLES);
    case 'import':
      return has(user, IMPORT_ROLES);
    default:
      return false;
  }
}

/** May this user see the hub at all? GET /specimens/hub's own rule. */
export const canSeeHub = (user) => !(user?.roles || []).includes('public')
  && (can(user, 'specimen:read') || can(user, 'specimen:register'));
